import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useCwSession } from '@/hooks/useCwSession';
import { isNative } from '@/lib/platform';
import { startRealtime } from '@/lib/realtime';
import { cwKeys } from '@/lib/cwCache';

/**
 * Держит живое соединение с Переписками (ActionCable), пока человек вошёл и состоит в кабинете проекта.
 * Возврат из фона (видимость страницы / состояние приложения) — переподключение и дозагрузка пропущенного.
 * Ничего не рисует.
 */
export function RealtimeServices() {
  const qc = useQueryClient();
  const { accountId, enabled, myUserId, profile } = useCwSession();
  const pubsubToken = profile.data?.pubsub_token;

  useEffect(() => {
    if (!enabled || accountId === null || myUserId === null || !pubsubToken) return;
    let rejections = 0;
    const handle = startRealtime({
      qc,
      accountId,
      userId: myUserId,
      pubsubToken,
      // Сервер не принял подписку — вероятно, ключ сменился. Обновляем профиль; не больше трёх раз подряд.
      onRejected: () => {
        rejections += 1;
        if (rejections <= 3) void qc.invalidateQueries({ queryKey: cwKeys.profile() });
      },
    });

    const wake = () => { if (document.visibilityState === 'visible') handle.reconnectNow(); };
    document.addEventListener('visibilitychange', wake);
    window.addEventListener('online', wake);

    let removed = false;
    const cleanups: Array<() => void> = [];
    if (isNative()) {
      void import('@capacitor/app').then(({ App }) => {
        void App.addListener('appStateChange', ({ isActive }) => { if (isActive) handle.reconnectNow(); })
          .then((h) => { if (removed) void h.remove(); else cleanups.push(() => void h.remove()); });
      }).catch(() => { /* без плагина остаётся видимость страницы */ });
    }

    return () => {
      removed = true;
      document.removeEventListener('visibilitychange', wake);
      window.removeEventListener('online', wake);
      cleanups.forEach((f) => f());
      handle.stop();
    };
  }, [qc, enabled, accountId, myUserId, pubsubToken]);

  return null;
}
